import type { SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import { ToastContainer, type ToastContainerProps } from 'react-toastify';

import { useStickyBottomStore } from '../store/stickyBottomStore';
import { useToastThemeStore } from '../store/toastThemeStore';
import { Colors } from '../utils/common';
import { Z_INDEX } from '../utils/common/zIndex';
import { TOAST_CONTENT_CLASS } from '../utils/hook/useToast';

import 'react-toastify/dist/ReactToastify.css';

/** 토스트 좌우 여백(px) */
const TOAST_SIDE_MARGIN_PX = 16;

/**
 * 토스트 좌우에 여백을 만드는 스타일.
 * react-toastify 11은 480px 이하에서 토스트 폭을 컨테이너 폭과 같게 잡아,
 * 좌우 여백을 줘도 토스트가 그만큼 화면 밖으로 밀려난다. 폭 상한을 여백만큼
 * 줄여 자리를 만든다. maxWidth 대신 width로 고정하면 PC에서 글자 길이만큼 줄어든다.
 */
const TOAST_SIDE_MARGIN_STYLE = {
  marginLeft: TOAST_SIDE_MARGIN_PX,
  marginRight: TOAST_SIDE_MARGIN_PX,
  maxWidth: `calc(100% - ${TOAST_SIDE_MARGIN_PX * 2}px)`,
} as const;

const GlobalToastContainer = () => {
  const toastTheme = useToastThemeStore.use.toastTheme();
  const toastZIndex = useToastThemeStore.use.toastZIndex();
  const maxBottom = useStickyBottomStore.use.maxBottom();
  const bottomOverride = useStickyBottomStore.use.bottomOverride();

  if (toastTheme === null) return null;

  const style: Record<
    typeof toastTheme,
    {
      box: SxProps;
      toastContainerProps: ToastContainerProps;
    }
  > = {
    'dark-brown': {
      box: {
        '.Toastify__toast': {
          fontFamily:
            'var(--font-pretendard), "Spoqa Han Sans Neo", sans-serif',
          boxShadow: '0 4px 8px 0 #0000001a, 0 4px 8px 0 #0000000d',
        },
        '.Toastify__close-button': {
          alignSelf: 'auto',
          color: '#C4C7CC',
        },
        [`.${TOAST_CONTENT_CLASS}`]: {
          padding: '0px',
          fontSize: '14px',
        },
        '.Toastify__toast-icon': {
          display: 'none',
        },
        '--toastify-text-color-light': '#FFFFFF',
        '--toastify-color-light': '#392E29',
        '--toastify-color-progress-success': '#795B4E',
        '--toastify-toast-width': '400px',
      },
      toastContainerProps: {
        toastStyle: {
          borderRadius: '10px',
          ...TOAST_SIDE_MARGIN_STYLE,
          marginTop: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        },
      },
    },
    'community-form': {
      box: {
        '--toastify-color-dark': Colors.BROWN[1300],
        '--toastify-color-progress-success': Colors.Brand[1],
      },
      toastContainerProps: {
        toastStyle: {
          borderRadius: '10px',
          ...TOAST_SIDE_MARGIN_STYLE,
          marginTop: 60,
          marginBottom: 16,
        },
        progressClassName: 'uglyus-toast-progress',
      },
    },
    'dark-info': {
      box: {
        '.Toastify__toast-container--bottom-center': {
          bottom:
            bottomOverride !== null
              ? bottomOverride
              : maxBottom > 0
                ? `${maxBottom}px`
                : 0,
          /**
           * 하단 CTA 위에서 퇴장하는 토스트를 컨테이너 경계에서 잘라,
           * CTA 뒤로 들어가듯 자연스럽게 사라지게 한다.
           */
          overflow: 'hidden',
        },
        '.Toastify__toast-container': {
          padding: 0,
          zIndex: toastZIndex ?? Z_INDEX.DARK_INFO_TOAST,
        },
        '.Toastify__toast': {
          backgroundColor: '#3B3532',
          color: '#FFFFFF',
          maxWidth: '480px',
          marginBottom: 0,
          borderRadius: 0,
          transition: 'transform 0.1s ease-out, opacity 0.1s ease-out',
        },
        '.Toastify__slide-enter--bottom-center': {
          animationDuration: '0.3s',
        },
        '.Toastify__close-button': {
          display: 'none',
        },
        [`.${TOAST_CONTENT_CLASS}`]: {
          /**
           * 토스트 폭을 채워야 textAlign 중앙 정렬이 눈에 보인다.
           * react-toastify 10까지는 내용을 감싸는 요소가 폭을 채웠지만(flex: 1 1 auto),
           * 11에는 그 요소가 없어 한 겹 감싼 이 상자가 글자 폭만큼 줄어든다.
           * 그러면 정렬 기준이 토스트의 justifyContent로 넘어가는데, 아래 주석대로
           * 그 값을 지킬 수 없어 폭을 채우는 쪽으로 되돌린다.
           */
          flex: 1,
          padding: '0px',
          fontSize: '14px',
          textAlign: 'center',
          whiteSpace: 'pre-line',
        },
        '.Toastify__toast-icon': {
          display: 'none',
        },
        '--toastify-text-color-light': '#FFFFFF',
        '--toastify-color-light': '#3B3532',
        '--toastify-toast-width': '480px',
      },
      /**
       * toastStyle을 두지 않는다 — useToastV2가 토스트마다 style 옵션(pointerEvents)을 넘기고,
       * react-toastify는 토스트에 style이 있으면 컨테이너 toastStyle을 통째로 버린다.
       * 이 테마에 걸 배치·모양은 위 box의 `.Toastify__toast` 규칙에 둔다.
       */
      toastContainerProps: {},
    },
  };

  return (
    <Box sx={style[toastTheme].box}>
      <ToastContainer {...style[toastTheme].toastContainerProps} />
    </Box>
  );
};

export default GlobalToastContainer;
